import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { Form, Input, Button } from "antd";
import { useDropzone } from "react-dropzone";
import {
  getAchatById,
  storeAchatFromClientDetailled,
} from "../../services/offers/payment";
import Loader from "../../components/loader/loader";
import "../../components/new_offers/styles.scss";
import Footer2 from "../../components/footer";
import { usePayment } from "../../middleware/PaymentContext";

function CompletePurchase() {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [backupCodes, setBackupCodes] = useState(["", "", "", ""]);
  const [whatsapp, setWhatsapp] = useState("");
  const [image, setImage] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { paymentCompleted, paymentData, clearPayment } = usePayment();
  const [isValidAccess, setIsValidAccess] = useState(false);

  const [couponCode, setCouponCode] = useState("");
  const [discount, setDiscount] = useState(0);

  useEffect(() => {
    console.log("Payment Data:", paymentData); // Debug payment data
    setCouponCode(localStorage.getItem("couponCode") ?? "");
    localStorage.removeItem("couponCode");
    setIsLoading(true);

    const checkAchat = async () => {
      try {
        const response = await getAchatById(id);
        setIsValidAccess(true);
        if (response.data?.achat) {
          if (response.data.achat.login) setLogin(response.data.achat.login);
          if (response.data.achat.password)
            setPassword(response.data.achat.password);
        }
      } catch (error) {
        if (error.response?.status === 401) {
          navigate("/login");
        } else {
          navigate("/404");
        }
      }
    };

    if (id) {
      checkAchat().then(() => setIsLoading(false));
    }
  }, []); // Added paymentData to dependencies

  const validateInputs = () => {
    const newErrors = {};

    if (!login.trim()) newErrors.login = t("Login is required");
    if (!password.trim()) newErrors.password = t("Password is required");
    if (backupCodes.some((code) => !code.trim()))
      newErrors.backupCodes = t("All backup codes are required");
    if (!whatsapp.trim()) newErrors.whatsapp = t("WhatsApp number is required");

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    console.log("Submit clicked");

    // Check if paymentData exists

    if (!validateInputs()) {
      toast.error(t("Please fill all required fields"));
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("login", login);
      formData.append("password", password);
      backupCodes.forEach((code, index) => {
        formData.append(`backup_codes[${index}]`, code);
      });
      if (image) formData.append("image", image);
      formData.append("offre_id", id);
      formData.append("whatsapp", whatsapp);
      formData.append("achat_id", id);

      formData.append("couponCode", couponCode);
      formData.append("discount", discount);

      console.log("FormData contents:");
      for (let [key, value] of formData.entries()) {
        console.log(key, value);
      }

      const response = await storeAchatFromClientDetailled(formData);
      console.log("API Response:", response);

      if (response.data?.achat?.id) {
        localStorage.removeItem("paymentData");
        localStorage.removeItem("couponCode");
        clearPayment();
        navigate(`/successful/${response.data.achat.id}/achat`);
      } else {
        throw new Error(t("Purchase failed - No achat ID returned"));
      }
    } catch (error) {
      console.error("Error details:", error);
      console.error("Error response:", error.response);

      // Handle specific error cases
      if (error.response?.data?.message === "Credentials already provided") {
        toast.error(
          t(
            "This purchase has already been completed. Please check your purchase history."
          )
        );
        navigate("/dashboard");
        return;
      }

      if (error.response?.data?.message === "Achat not found") {
        toast.error(t("Purchase record not found. Please contact support."));
        navigate("/dashboard");
        return;
      }

      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.message) {
        toast.error(error.message);
      } else {
        toast.error(t("Failed to complete purchase"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const beforeUpload = (file) => {
    const validTypes = [
      "image/jpeg",
      "image/png",
      "image/jpg",
      "image/gif",
      "image/svg+xml",
    ];
    const isValidType = validTypes.includes(file.type);

    if (!isValidType) {
      toast.error(t("invalid file type"));
      return false;
    }

    setImage(file);
    return false;
  };

  const onDrop = useCallback((acceptedFiles) => {
    acceptedFiles.forEach((file) => {
      beforeUpload(file);
    });
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      "image/*": [".jpeg", ".png", ".jpg", ".gif", ".svg"],
    },
    maxFiles: 1,
  });

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <Loader />
      </div>
    );
  }

  // Add a check for paymentData

  return (
    <>
      <div className="page-about home-1">
        <section className="tf-section tf-about">
          <div className="tf-container">
            <div className="row">
              <div className="col-lg-8 mx-auto">
                <div
                  className="card p-4 mt-3"
                  style={{
                    border: "none",
                    borderRadius: "15px",
                    boxShadow: "0 0 10px rgba(0,0,0,0.1)",
                    backgroundColor: "var(--primary-color8) !important",
                  }}
                >
                  <div className="payment-complete-form">
                    <div
                      className="tf-heading wow fadeInUp"
                      style={{ textAlign: "left" }}
                    >
                      <h2 className="heading mt-3">{t("complete_purchase")}</h2>
                      <p className="sub-heading">
                        {t("provide_required_details")}
                      </p>
                    </div>

                    <Form layout="vertical">
                      <Form.Item
                        label={t("EA Account")}
                        required
                        validateStatus={errors.login ? "error" : ""}
                        help={errors.login}
                      >
                        <Input
                          type="text"
                          value={login}
                          onChange={(e) => setLogin(e.target.value)}
                          status={errors.login ? "error" : ""}
                        />
                      </Form.Item>
                      <Form.Item
                        label={t("EA Password")}
                        required
                        validateStatus={errors.password ? "error" : ""}
                        help={errors.password}
                      >
                        <Input
                          type="text"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          status={errors.password ? "error" : ""}
                        />
                      </Form.Item>
                      <Form.Item
                        label={t("backup codes")}
                        validateStatus={errors.backupCodes ? "error" : ""}
                        help={errors.backupCodes}
                      >
                        <div className="row">
                          {backupCodes.map((code, index) => (
                            <div key={index} className="col-lg-6">
                              <Input
                                type="text"
                                value={code}
                                onChange={(e) => {
                                  const newCodes = [...backupCodes];
                                  newCodes[index] = e.target.value;
                                  setBackupCodes(newCodes);
                                }}
                                style={{
                                  marginBottom: "10px",
                                  color: "#000000",
                                  backgroundColor: "#ffffff",
                                  border: "1px solid #d9d9d9",
                                  // Optional: to ensure black text in all states
                                  WebkitTextFillColor: "var(--input-color)", // For WebKit browsers
                                  opacity: 1, // Ensure full opacity
                                }}
                                status={errors.backupCodes ? "error" : ""}
                                // If the Input gets disabled styling, override it
                                className={
                                  errors.backupCodes
                                    ? "ant-input-status-error"
                                    : ""
                                }
                              />
                            </div>
                          ))}
                        </div>
                      </Form.Item>
                      <Form.Item
                        label={t("whatsapp")}
                        required
                        validateStatus={errors.whatsapp ? "error" : ""}
                        help={errors.whatsapp}
                      >
                        <Input
                          type="text"
                          value={whatsapp}
                          onChange={(e) => setWhatsapp(e.target.value)}
                          status={errors.whatsapp ? "error" : ""}
                        />
                      </Form.Item>
                      <Form.Item label={t("upload image")}>
                        <div
                          {...getRootProps({
                            className: "custom-file",
                            style: { marginBottom: "30px !important" },
                          })}
                        >
                          <input {...getInputProps()} />
                          <div className="upload-zone">
                            <span style={{ color: "var(--primary-color3)" }}>
                              {t(
                                "drag & drop your file here or click to upload"
                              )}
                            </span>
                          </div>
                        </div>
                        {image && <p>{image.name}</p>}
                      </Form.Item>
                      <Form.Item>
                        <button
                          className="new_offers_buy_button"
                          onClick={handleSubmit}
                          disabled={isSubmitting}
                        >
                          {isSubmitting
                            ? t("Submitting...")
                            : t("Complete_Purchase")}
                        </button>
                      </Form.Item>
                    </Form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      <Footer2 />
      <ToastContainer position="bottom-right" autoClose={5000} />
    </>
  );
}

export default CompletePurchase;
